import React, { memo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { styled } from "@mui/material/styles"
import Stack from "@mui/material/Stack"
import { useAppSelector } from "appRedux/hooks"
import { CONNECTION_BUTTON, EDIT, FOLLOWERS, FOLLOWINGS, ITEMS, LISTS, MEDIUM, PROFILE, SCROLL_THRESHOLD } from "constants/index"
import { wishListActions } from "constants/options"
import { FOLLOW_REQUESTED, FOLLOWABLE } from "constants/notifications"
import { SHOW_SHARE_PROFILE_MODAL } from "constants/events"
import useEventListener from "hooks/useEventListener"
import { useAuth } from "context/AuthContext"
import ShareIcon from "components/atoms/_icons/ShareIcon"
import Tabs from "components/atoms/Tabs"
import CTA from "components/atoms/CTA"
import ProfileBanner from "components/molecules/ProfileBanner"
import ProfileConnectionSection from "components/molecules/ProfileConnectionSection"
import WishItemsList from "components/organisms/WishItemsList"
import FollowersList from "components/organisms/FollowersList"
import FollowingsList from "components/organisms/FollowingsList"
import WishListsList from "components/templates/WishListsList"
import withRoleBasedPermission from "components/HOCs/withRoleBasedPermission"

function renderTabTitle(label: string, count: string | number) {
  return <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
    <span>{count}</span>
    <span style={{ fontSize: "1rem" }}>{label}</span>
  </div>
}

type ProfilePageTemplateProps = {
  userInfo: Profile | null;
  loading: boolean;
}

export default memo(function ProfilePageTemplate(props: ProfilePageTemplateProps) {
  const { userInfo, loading } = props

  const [isMinimized, setIsMinimized] = useState<boolean>(false)
  const [isFixed, setIsFixed] = useState<boolean>(false)

  const selectedTabIndex = useAppSelector((state) => state.profileTabs.selectedTabIndex)

  const { t } = useTranslation()
  const navigate = useNavigate()

  const { isAuthenticated, user } = useAuth()

  useEventListener(document, "scroll", handleScroll)

  function handleScroll() {
    if (!userInfo?.handle) return
    const scrollY = window.scrollY
    const activeTab = document.getElementById(`tabpanel-${selectedTabIndex[userInfo?.handle]}`)
    if (!activeTab) return
    if ((activeTab.scrollHeight - 252) <= window.innerHeight) {
      setIsMinimized(false)
      setIsFixed(false)
    } else {
      setIsMinimized(scrollY > SCROLL_THRESHOLD)
      setIsFixed(scrollY > SCROLL_THRESHOLD)
    }
  }

  function getTabs() {
    return [
      {
        label: getListsTabTitle(),
        disabled: false,
        type: LISTS,
        content: <WishListsList
          moreButtonActions={wishListActions}
          totalCount={userInfo?.wishlistCount}
        />
      },
      {
        label: getItemsTabTitle(),
        disabled: false,
        type: ITEMS,
        content: <WishItemsList
          showAddNewItemButton={false}
          totalCount={userInfo?.itemsCount}
        />
      },
      {
        label: getFollowersTabTitle(),
        disabled: [FOLLOWABLE, FOLLOW_REQUESTED].includes(userInfo?.followStatus || ""),
        type: FOLLOWERS,
        content: <FollowersList id={userInfo?.id}/>
      },
      {
        label: getFollowingsTabTitle(),
        type: FOLLOWINGS,
        disabled: [FOLLOWABLE, FOLLOW_REQUESTED].includes(userInfo?.followStatus || ""),
        content: <FollowingsList id={userInfo?.id}/>
      }
    ]
  }

  function getListsTabTitle() {
    return renderTabTitle(t("collections.title"), userInfo?.wishlistCount || 0)
  }

  function getItemsTabTitle() {
    return renderTabTitle(t("items.title"), userInfo?.itemsCount || 0)
  }

  function getFollowersTabTitle() {
    return renderTabTitle(t("connections.followers"), userInfo?.followerCount || 0)
  }

  function getFollowingsTabTitle() {
    return renderTabTitle(t("connections.followings"), userInfo?.followingCount || 0)
  }

  function navigateToUpdateProfilePage() {
    if (!isAuthenticated || !user) return
    navigate("/account/edit")
  }

  function handleShareIconClicked() {
    document.dispatchEvent(new CustomEvent(SHOW_SHARE_PROFILE_MODAL))
  }

  return <Stack
    width={"100%"}
    height={"100%"}
    alignItems="start"
    flexGrow={1}
    style={{
      transition: "1s",
      marginBottom: "16px"
    }}
  >
    <ProfileBanner
      userProfileData={userInfo || null}
      loading={loading}
      isMinimized={isMinimized}
      isFixed={isFixed}
    />

    {!loading && <>
        <EditProfileButtonWithRoleBasedPermission
            resource={PROFILE}
            action={EDIT}
        >
            <CTA
                variant="contained"
                size={MEDIUM}
                onClick={navigateToUpdateProfilePage}
                style={{ width: "50%" }}
            >
              {t("user.updateProfile")}
            </CTA>
            <ShareIconWrapper onClick={handleShareIconClicked}>
                <ShareIcon/>
            </ShareIconWrapper>
        </EditProfileButtonWithRoleBasedPermission>

      {(!!userInfo?.id &&
        userInfo?.followStatus !== null &&
        userInfo?.requestedToFollow !== null) && <ProfileConnectionSectionWithRoleBasedPermission
          resource={PROFILE}
          action={CONNECTION_BUTTON}
          userId={userInfo?.id}
          handle={userInfo?.handle}
          followStatus={userInfo?.followStatus}
          requestedToFollow={userInfo?.requestedToFollow}
          isCloseFriend={userInfo?.isCloseFriend}
      />}
    </>}

    {(userInfo?.handle && selectedTabIndex[userInfo?.handle] > -1) && <TabsWrapper>
        <Tabs
            data={getTabs()}
            selectedTabIndex={selectedTabIndex[userInfo?.handle]}
            isMinimized={isMinimized}
            isFixed={isFixed}
        />
    </TabsWrapper>}
  </Stack>
})

const EditProfileButtonWrapper = styled(Stack)(({ theme }) => ({
  width: "100%",
  flexDirection: "row-reverse",
  justifyContent: "center",
  alignItems: "center",
  gap: "8px",
  marginBottom: "8px",

  "& button": {
    backgroundColor: `${theme.palette.primary["2"]} !important`,
    color: `${theme.palette.primary["6"]} !important`
  }
}))

const ShareIconWrapper = styled(Stack)(({ theme }) => ({
  width: "40px",
  height: "40px",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: theme.palette.gray[5],
  borderRadius: "50%",
  cursor: "pointer"
}))

const TabsWrapper = styled(Stack)(({ theme }) => ({
  width: "100%",
  flexGrow: 1
}))

const EditProfileButtonWithRoleBasedPermission = withRoleBasedPermission(EditProfileButtonWrapper)

const ProfileConnectionSectionWithRoleBasedPermission = withRoleBasedPermission(ProfileConnectionSection)
